import React, { useState, useCallback, useEffect } from "react";
import { Modal } from "newui/components/ui/Modal";
import { RichTextEditor } from "newui/components/ui/RichTextEditor";

interface ScoringInstructionsEditModalValues {
	min: number;
	max: number;
	isSingleValue: boolean;
	description: string;
}

interface ScoringInstructionsEditModalProps {
	initialValues: ScoringInstructionsEditModalValues;
	isOpen: boolean;
	scoreLabel: string;
	totalScore: number;
	onClose: () => void;
	onSave: (values: ScoringInstructionsEditModalValues) => void;
}

export const ScoringInstructionsEditModal: React.FC<ScoringInstructionsEditModalProps> = ({
	initialValues,
	isOpen,
	scoreLabel,
	totalScore,
	onClose,
	onSave,
}) => {
	const [draft, setDraft] = useState<ScoringInstructionsEditModalValues>(initialValues);

	useEffect(() => {
		if (isOpen) {
			setDraft(initialValues);
		}
	}, [isOpen, initialValues]);

	const handleSave = useCallback(() => {
		onSave(draft);
		onClose();
	}, [draft, onSave, onClose]);

	const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(e.target.value, 10);
		if (!isNaN(value)) {
			if (draft.isSingleValue) {
				setDraft((prev) => ({ ...prev, min: value, max: value }));
			} else {
				setDraft((prev) => ({ ...prev, min: value }));
			}
		}
	};

	const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(e.target.value, 10);
		if (!isNaN(value)) {
			setDraft((prev) => ({ ...prev, max: value }));
		}
	};

	const handleToggleType = () => {
		setDraft((prev) => {
			if (prev.isSingleValue) {
				return { ...prev, isSingleValue: false };
			}
			return { ...prev, isSingleValue: true, max: prev.min };
		});
	};

	const handleDescriptionChange = useCallback((html: string) => {
		setDraft((prev) => ({ ...prev, description: html }));
	}, []);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Edit Score Instruction"
			iconType="edit"
			size="xl"
			closeOnEsc
			primaryButton={{
				text: "Save",
				onClick: handleSave,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: onClose,
			}}
		>
			<div className="flex flex-col gap-4">
				<div className="flex items-end gap-4">
					<div className="flex flex-col gap-1">
						<label className="font-roboto text-sm font-medium text-gray-700">
							{draft.isSingleValue ? "Score" : "Min Score"}
						</label>
						<input
							type="number"
							value={draft.min}
							onChange={handleMinChange}
							min={0}
							max={totalScore}
							className="w-24 rounded border border-gray-300 px-3 py-2 font-roboto text-sm focus:border-royal-blue focus:outline-none"
							data-testid="edit-modal-min"
						/>
					</div>
					{!draft.isSingleValue && (
						<>
							<span className="pb-2 font-roboto text-sm text-gray-400">–</span>
							<div className="flex flex-col gap-1">
								<label className="font-roboto text-sm font-medium text-gray-700">
									Max Score
								</label>
								<input
									type="number"
									value={draft.max}
									onChange={handleMaxChange}
									min={0}
									max={totalScore}
									className="w-24 rounded border border-gray-300 px-3 py-2 font-roboto text-sm focus:border-royal-blue focus:outline-none"
									data-testid="edit-modal-max"
								/>
							</div>
						</>
					)}
					<button
						type="button"
						onClick={handleToggleType}
						className="rounded border border-gray-300 px-3 py-2 font-roboto text-xs text-gray-600 hover:bg-gray-50"
						data-testid="edit-modal-toggle-type"
					>
						{draft.isSingleValue ? "Use Range" : "Use Single Value"}
					</button>
				</div>

				<div>
					<label className="mb-1 block font-roboto text-sm font-medium text-gray-700">
						Description
					</label>
					<RichTextEditor
						value={draft.description}
						onChange={handleDescriptionChange}
						placeholder="Enter scoring description..."
						minHeight="10rem"
						testId="edit-modal-editor"
					/>
				</div>
			</div>
		</Modal>
	);
};
